import React, { useCallback, useEffect, useState } from 'react';
import {
  Avatar,
  Box,
  Card,
  Chip,
  CircularProgress,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  alpha,
  Button,
} from '@mui/material';
import { useDispatch } from 'react-redux';
import { enqueueSnackbar } from 'notistack';
import { saveAs } from 'file-saver';
import * as XLSX from 'xlsx';
import dayjs from 'dayjs';

import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import RefreshOutlinedIcon from '@mui/icons-material/RefreshOutlined';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import EditNoteOutlinedIcon from '@mui/icons-material/EditNoteOutlined';
import PaymentOutlinedIcon from '@mui/icons-material/PaymentOutlined';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';

import OrderStatusModal from './OrderStatusModal';
import OrderDetailsModal from './OrderDetailsModal';
import CreateOrderModal from './CreateOrderModal';
import {
  confirmExperienceOrderAdmin,
  getAdminExperienceOrders,
} from '../../actions/experienceAuth';

const ORDER_STATUS_OPTIONS = [
  { value: 'ALL', label: 'All Order Statuses' },
  { value: 'PENDING_FOR_APPROVAL', label: 'Pending Approval' },
  { value: 'VERIFY', label: 'Verify (Slot/Vendor)' },
  { value: 'NEED_TO_PAYMENT', label: 'Need to Payment' },
  { value: 'CONFIRM', label: 'Confirm (Confirmed & Paid)' },
  { value: 'COMPLETED', label: 'Completed' },
  { value: 'CANCELLED', label: 'Cancelled' },
];

const PAYMENT_STATUS_OPTIONS = [
  { value: 'ALL', label: 'All Payment Statuses' },
  { value: 'PENDING', label: 'Pending Payment' },
  { value: 'PAID', label: 'Paid / Success' },
  { value: 'FAILED', label: 'Failed' },
];

const PanelCard = ({ children, sx = {} }) => (
  <Card
    elevation={0}
    sx={{
      borderRadius: '24px',
      border: '1px solid',
      borderColor: alpha('#11409B', 0.08),
      bgcolor: '#fff',
      boxShadow: '0 4px 32px rgba(15, 23, 42, 0.06)',
      overflow: 'hidden',
      ...sx,
    }}
  >
    {children}
  </Card>
);

const fieldSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: '12px',
    bgcolor: '#fff',
    '& fieldset': { borderColor: alpha('#11409B', 0.2) },
    '&:hover fieldset': { borderColor: '#11409B' },
  },
};

const getStatusColor = (status) => {
  const s = String(status || '').toUpperCase();
  if (s === 'PENDING_FOR_APPROVAL') return '#d97706';
  if (s === 'VERIFY') return '#2563eb';
  if (s === 'NEED_TO_PAYMENT') return '#7c3aed';
  if (s === 'CONFIRM') return '#059669';
  if (s === 'COMPLETED') return '#0d9488';
  if (s === 'CANCELLED') return '#e11d48';
  return '#64748b';
};

const getPaymentBadge = (status) => {
  const s = String(status || '').toUpperCase();
  if (s === 'PAID' || s === 'SUCCESS') {
    return { color: '#059669', bg: alpha('#059669', 0.12), label: 'PAID' };
  }
  if (s === 'PENDING') {
    return { color: '#d97706', bg: alpha('#d97706', 0.12), label: 'PENDING' };
  }
  return { color: '#dc2626', bg: alpha('#dc2626', 0.12), label: s || 'FAILED' };
};

const ExperienceOrdersTab = ({ onStatsUpdate }) => {
  const dispatch = useDispatch();

  const [orders, setOrders] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);

  // Filters
  const [search, setSearch] = useState('');
  const [orderStatusFilter, setOrderStatusFilter] = useState('ALL');
  const [paymentStatusFilter, setPaymentStatusFilter] = useState('ALL');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');

  // Modals state
  const [statusModalOpen, setStatusModalOpen] = useState(false);
  const [selectedForStatus, setSelectedForStatus] = useState(null);
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const [selectedForDetails, setSelectedForDetails] = useState(null);
  const [createOrderOpen, setCreateOrderOpen] = useState(false);

  const fetchOrders = useCallback(() => {
    setLoading(true);

    const params = {
      page,
      size: pageSize,
      ...(search.trim() ? { search: search.trim() } : {}),
      ...(orderStatusFilter !== 'ALL' ? { orderStatus: orderStatusFilter } : {}),
      ...(paymentStatusFilter !== 'ALL' ? { paymentStatus: paymentStatusFilter } : {}),
      ...(fromDate ? { from: fromDate } : {}),
      ...(toDate ? { to: toDate } : {}),
    };

    dispatch(
      getAdminExperienceOrders(
        params,
        (res) => {
          setLoading(false);
          const data = res?.data?.data || res?.data || [];
          const list = Array.isArray(data)
            ? data
            : data?.content || data?.orders || data?.items || [];
          const total =
            res?.data?.totalElements ??
            res?.data?.total ??
            res?.data?.results ??
            data?.totalElements ??
            list.length;

          setOrders(list);
          setTotalCount(total);

          if (onStatsUpdate) {
            const pendingApproval = list.filter(
              (o) => (o.orderStatus || o.status) === 'PENDING_FOR_APPROVAL'
            ).length;
            const verifyCount = list.filter((o) => (o.orderStatus || o.status) === 'VERIFY').length;
            const needToPay = list.filter(
              (o) => (o.orderStatus || o.status) === 'NEED_TO_PAYMENT'
            ).length;
            const confirmedCount = list.filter(
              (o) => (o.orderStatus || o.status) === 'CONFIRM'
            ).length;

            onStatsUpdate({
              totalOrders: total,
              pendingApproval,
              underVerification: verifyCount,
              needPayment: needToPay,
              confirmedOrders: confirmedCount,
            });
          }
        },
        (err) => {
          setLoading(false);
          const msg = err?.response?.data?.message || err?.message || 'Failed to fetch experience orders';
          enqueueSnackbar(msg, { variant: 'error' });
        }
      )
    );
  }, [dispatch, page, pageSize, search, orderStatusFilter, paymentStatusFilter, fromDate, toDate, onStatsUpdate]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  const handleClearFilters = () => {
    setSearch('');
    setOrderStatusFilter('ALL');
    setPaymentStatusFilter('ALL');
    setFromDate('');
    setToDate('');
    setPage(0);
  };

  const handleQuickConfirm = (order) => {
    const id = order.id || order._id || order.orderId;
    dispatch(
      confirmExperienceOrderAdmin(
        id,
        { remarks: 'Admin direct confirmed order' },
        () => {
          enqueueSnackbar(`Order #${id} confirmed and marked as PAID`, { variant: 'success' });
          fetchOrders();
        },
        (err) => {
          const msg = err?.response?.data?.message || 'Failed to confirm order';
          enqueueSnackbar(msg, { variant: 'error' });
        }
      )
    );
  };

  const handleExportExcel = () => {
    if (!orders.length) {
      enqueueSnackbar('No orders to export', { variant: 'warning' });
      return;
    }

    const exportData = orders.map((o) => ({
      'Order ID': o.id || o._id || o.orderId,
      'Customer Name': o.userName || o.customerName || o.user?.name,
      'Customer Email': o.userEmail || o.customerEmail || o.user?.email,
      'Customer Phone': o.userPhone || o.customerPhone || o.user?.phone,
      'Experience Title': o.experienceTitle || o.experience?.title || o.title,
      'Booking Date': o.bookingDate || o.slotDate || o.date,
      'Slots Booked': o.slotsBooked || o.slots || 1,
      'Total Amount': o.totalAmount ?? o.amount ?? 0,
      'Payment Status': o.paymentStatus || 'PENDING',
      'Order Status': o.orderStatus || o.status || 'PENDING_FOR_APPROVAL',
      'Created Date': o.createdAt ? dayjs(o.createdAt).format('YYYY-MM-DD HH:mm') : '—',
    }));

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Experience_Orders');
    const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    saveAs(new Blob([excelBuffer], { type: 'application/octet-stream' }), 'experience_orders.xlsx');
  };

  const totalPages = Math.ceil(totalCount / pageSize) || 1;

  return (
    <Box>
      {/* Toolbar & Filters */}
      <PanelCard sx={{ mb: 3, p: 2.5 }}>
        <Grid container spacing={2} alignItems="center">
          {/* Search */}
          <Grid item xs={12} md={3.5}>
            <TextField
              size="small"
              placeholder="Search by Order ID, customer, experience..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(0);
              }}
              fullWidth
              sx={fieldSx}
              InputProps={{
                startAdornment: <SearchOutlinedIcon fontSize="small" sx={{ color: '#94a3b8', mr: 1 }} />,
              }}
            />
          </Grid>

          {/* Order Status Filter */}
          <Grid item xs={6} md={2.5}>
            <FormControl fullWidth size="small" sx={fieldSx}>
              <InputLabel>Order Status</InputLabel>
              <Select
                value={orderStatusFilter}
                label="Order Status"
                onChange={(e) => {
                  setOrderStatusFilter(e.target.value);
                  setPage(0);
                }}
              >
                {ORDER_STATUS_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Payment Status Filter */}
          <Grid item xs={6} md={2}>
            <FormControl fullWidth size="small" sx={fieldSx}>
              <InputLabel>Payment Status</InputLabel>
              <Select
                value={paymentStatusFilter}
                label="Payment Status"
                onChange={(e) => {
                  setPaymentStatusFilter(e.target.value);
                  setPage(0);
                }}
              >
                {PAYMENT_STATUS_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Date Range & Actions */}
          <Grid item xs={12} md={4}>
            <Stack direction="row" spacing={1} justifyContent="flex-end" alignItems="center">
              <TextField
                size="small"
                type="date"
                label="From"
                value={fromDate}
                onChange={(e) => {
                  setFromDate(e.target.value);
                  setPage(0);
                }}
                InputLabelProps={{ shrink: true }}
                sx={{ width: 130, ...fieldSx }}
              />
              <TextField
                size="small"
                type="date"
                label="To"
                value={toDate}
                onChange={(e) => {
                  setToDate(e.target.value);
                  setPage(0);
                }}
                InputLabelProps={{ shrink: true }}
                sx={{ width: 130, ...fieldSx }}
              />

              <Tooltip title="Refresh">
                <IconButton
                  onClick={fetchOrders}
                  sx={{
                    bgcolor: alpha('#11409B', 0.08),
                    color: '#11409B',
                    borderRadius: '12px',
                    '&:hover': { bgcolor: alpha('#11409B', 0.16) },
                  }}
                >
                  <RefreshOutlinedIcon />
                </IconButton>
              </Tooltip>

              <Tooltip title="Export to Excel">
                <IconButton
                  onClick={handleExportExcel}
                  sx={{
                    bgcolor: alpha('#11409B', 0.08),
                    color: '#11409B',
                    borderRadius: '12px',
                    '&:hover': { bgcolor: alpha('#11409B', 0.16) },
                  }}
                >
                  <FileDownloadOutlinedIcon />
                </IconButton>
              </Tooltip>
            </Stack>
          </Grid>

          {/* Sub Toolbar: Clear & Create Test Order */}
          <Grid item xs={12}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              {(search || orderStatusFilter !== 'ALL' || paymentStatusFilter !== 'ALL' || fromDate || toDate) && (
                <Button
                  onClick={handleClearFilters}
                  size="small"
                  sx={{ textTransform: 'none', fontWeight: 600, color: '#dc2626' }}
                >
                  Reset Order Filters
                </Button>
              )}
              <Box sx={{ flexGrow: 1 }} />
              <Button
                variant="outlined"
                startIcon={<AddCircleOutlineIcon />}
                onClick={() => setCreateOrderOpen(true)}
                sx={{
                  borderRadius: '12px',
                  textTransform: 'none',
                  fontWeight: 700,
                  color: '#11409B',
                  borderColor: alpha('#11409B', 0.3),
                }}
              >
                Create Test Booking
              </Button>
            </Stack>
          </Grid>
        </Grid>
      </PanelCard>

      {/* Orders Table */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
          <CircularProgress size={40} sx={{ color: '#11409B' }} />
        </Box>
      ) : orders.length === 0 ? (
        <PanelCard sx={{ p: 6, textAlign: 'center' }}>
          <Typography variant="h6" sx={{ color: '#475569', fontWeight: 700 }}>
            No experience orders found
          </Typography>
          <Typography variant="body2" sx={{ color: '#94a3b8', mt: 0.5, mb: 2 }}>
            There are no booking orders matching your current filter criteria.
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddCircleOutlineIcon />}
            onClick={() => setCreateOrderOpen(true)}
            sx={{
              borderRadius: '12px',
              textTransform: 'none',
              fontWeight: 700,
              background: 'linear-gradient(135deg, #0c3483 0%, #11409B 100%)',
            }}
          >
            Create First Order
          </Button>
        </PanelCard>
      ) : (
        <PanelCard>
          <TableContainer>
            <Table sx={{ minWidth: 900 }}>
              <TableHead sx={{ bgcolor: alpha('#11409B', 0.04) }}>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700, color: '#1e293b' }}>Order ID</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: '#1e293b' }}>Customer</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: '#1e293b' }}>Experience</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: '#1e293b' }}>Booking Info</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: '#1e293b' }}>Amount</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: '#1e293b' }}>Payment</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: '#1e293b' }}>Order Status</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: '#1e293b' }} align="right">
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {orders.map((o) => {
                  const id = o.id || o._id || o.orderId;
                  const status = String(o.orderStatus || o.status || 'PENDING_FOR_APPROVAL').toUpperCase();
                  const paymentStatus = String(o.paymentStatus || 'PENDING').toUpperCase();
                  const paymentBadge = getPaymentBadge(paymentStatus);

                  const customerName = o.userName || o.customerName || o.user?.name || 'Customer';
                  const customerPhone = o.userPhone || o.customerPhone || o.user?.phone || '—';
                  const customerEmail = o.userEmail || o.customerEmail || o.user?.email || '';

                  const expTitle = o.experienceTitle || o.experience?.title || o.title || 'Curated Experience';
                  const expCategory = o.experienceCategory || o.experience?.category || o.category || '';

                  const bookingDate = o.bookingDate || o.slotDate || o.date;
                  const slots = o.slotsBooked || o.slots || 1;
                  const total = o.totalAmount ?? o.amount ?? o.price ?? 0;
                  const createdAt = o.createdAt ? dayjs(o.createdAt).format('DD MMM, hh:mm A') : '—';

                  const canQuickConfirm = status === 'VERIFY' || status === 'NEED_TO_PAYMENT';

                  return (
                    <TableRow key={id} hover sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                      {/* Order ID & Date */}
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 800, color: '#11409B' }}>
                          #{id}
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#94a3b8' }}>
                          {createdAt}
                        </Typography>
                      </TableCell>

                      {/* Customer Info */}
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 700, color: '#0f172a' }}>
                          {customerName}
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#64748b', display: 'block' }}>
                          {customerPhone}
                        </Typography>
                        {customerEmail && (
                          <Typography variant="caption" sx={{ color: '#94a3b8' }}>
                            {customerEmail}
                          </Typography>
                        )}
                      </TableCell>

                      {/* Experience Info */}
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 700, color: '#334155' }}>
                          {expTitle}
                        </Typography>
                        {expCategory && (
                          <Chip
                            label={expCategory}
                            size="small"
                            sx={{
                              height: 18,
                              fontSize: '0.65rem',
                              fontWeight: 700,
                              bgcolor: alpha('#11409B', 0.08),
                              color: '#11409B',
                              mt: 0.3,
                            }}
                          />
                        )}
                      </TableCell>

                      {/* Booking Info */}
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#475569' }}>
                          {bookingDate ? dayjs(bookingDate).format('DD MMM YYYY') : '—'}
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#7c3aed', fontWeight: 700 }}>
                          {slots} {slots === 1 ? 'spot' : 'spots'} booked
                        </Typography>
                      </TableCell>

                      {/* Amount */}
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 800, color: '#16a34a' }}>
                          ₹{Number(total).toLocaleString('en-IN')}
                        </Typography>
                      </TableCell>

                      {/* Payment Status */}
                      <TableCell>
                        <Chip
                          label={paymentBadge.label}
                          size="small"
                          sx={{
                            fontWeight: 800,
                            fontSize: '0.7rem',
                            bgcolor: paymentBadge.bg,
                            color: paymentBadge.color,
                            border: `1px solid ${alpha(paymentBadge.color, 0.3)}`,
                          }}
                        />
                      </TableCell>

                      {/* Order Status (State Machine) */}
                      <TableCell>
                        <Chip
                          label={status}
                          size="small"
                          sx={{
                            fontWeight: 800,
                            fontSize: '0.72rem',
                            bgcolor: alpha(getStatusColor(status), 0.12),
                            color: getStatusColor(status),
                            border: `1px solid ${alpha(getStatusColor(status), 0.35)}`,
                          }}
                        />
                      </TableCell>

                      {/* Actions */}
                      <TableCell align="right">
                        <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                          {canQuickConfirm && (
                            <Tooltip title="Direct Confirm & Pay">
                              <IconButton
                                size="small"
                                onClick={() => handleQuickConfirm(o)}
                                sx={{
                                  color: '#059669',
                                  bgcolor: alpha('#059669', 0.08),
                                  '&:hover': { bgcolor: alpha('#059669', 0.16) },
                                }}
                              >
                                <PaymentOutlinedIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}

                          <Tooltip title="View Order Details">
                            <IconButton
                              size="small"
                              onClick={() => {
                                setSelectedForDetails(o);
                                setDetailsModalOpen(true);
                              }}
                              sx={{
                                color: '#2563eb',
                                bgcolor: alpha('#2563eb', 0.08),
                                '&:hover': { bgcolor: alpha('#2563eb', 0.16) },
                              }}
                            >
                              <VisibilityOutlinedIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>

                          <Tooltip title="Manage Lifecycle Status">
                            <IconButton
                              size="small"
                              onClick={() => {
                                setSelectedForStatus(o);
                                setStatusModalOpen(true);
                              }}
                              sx={{
                                color: '#11409B',
                                bgcolor: alpha('#11409B', 0.08),
                                '&:hover': { bgcolor: alpha('#11409B', 0.16) },
                              }}
                            >
                              <EditNoteOutlinedIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </PanelCard>
      )}

      {/* Pagination Bar */}
      {totalCount > 0 && (
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{ mt: 3, px: 1 }}
          flexWrap="wrap"
          gap={2}
        >
          <Typography variant="body2" sx={{ color: '#64748b' }}>
            Showing <strong>{orders.length}</strong> of <strong>{totalCount}</strong> orders
          </Typography>

          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="caption" sx={{ color: '#64748b', mr: 1 }}>
              Per page:
            </Typography>
            <Select
              size="small"
              value={pageSize}
              onChange={(e) => {
                setPageSize(Number(e.target.value));
                setPage(0);
              }}
              sx={{ height: 32, borderRadius: '8px', ...fieldSx }}
            >
              <MenuItem value={10}>10</MenuItem>
              <MenuItem value={20}>20</MenuItem>
              <MenuItem value={50}>50</MenuItem>
            </Select>

            <IconButton
              disabled={page === 0}
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              sx={{ borderRadius: '8px', border: '1px solid rgba(0,0,0,0.1)' }}
            >
              <KeyboardArrowLeftIcon fontSize="small" />
            </IconButton>

            <Typography variant="body2" sx={{ fontWeight: 700, px: 1 }}>
              {page + 1} / {totalPages}
            </Typography>

            <IconButton
              disabled={page + 1 >= totalPages}
              onClick={() => setPage((p) => p + 1)}
              sx={{ borderRadius: '8px', border: '1px solid rgba(0,0,0,0.1)' }}
            >
              <KeyboardArrowRightIcon fontSize="small" />
            </IconButton>
          </Stack>
        </Stack>
      )}

      {/* Status Transition Modal */}
      <OrderStatusModal
        open={statusModalOpen}
        handleClose={() => {
          setStatusModalOpen(false);
          setSelectedForStatus(null);
        }}
        order={selectedForStatus}
        onSuccess={fetchOrders}
      />

      {/* Order Details Modal */}
      <OrderDetailsModal
        open={detailsModalOpen}
        handleClose={() => {
          setDetailsModalOpen(false);
          setSelectedForDetails(null);
        }}
        order={selectedForDetails}
        onOpenStatusModal={(ord) => {
          setSelectedForStatus(ord);
          setStatusModalOpen(true);
        }}
      />

      {/* Create Order Modal */}
      <CreateOrderModal
        open={createOrderOpen}
        handleClose={() => setCreateOrderOpen(false)}
        onSuccess={fetchOrders}
      />
    </Box>
  );
};

export default ExperienceOrdersTab;
